import { Injectable } from '@angular/core';

export interface PermissionStatus {
  granted: boolean;
  denied: boolean;
  prompt: boolean;
  error?: string;
}

export interface DevicePermissions {
  geolocation: PermissionStatus;
  camera: PermissionStatus;
  notifications: PermissionStatus;
  persistentStorage: PermissionStatus;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private permissionStates: DevicePermissions = {
    geolocation: { granted: false, denied: false, prompt: true },
    camera: { granted: false, denied: false, prompt: true },
    notifications: { granted: false, denied: false, prompt: true },
    persistentStorage: { granted: false, denied: false, prompt: true }
  };

  constructor() {
    this.initializePermissionStates();
  }

  private async initializePermissionStates(): Promise<void> {
    if (!navigator.permissions) {
      console.warn('Permissions API no está disponible en este navegador');
      return;
    }

    try {
      // Check geolocation permission
      const geoPermission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      this.updatePermissionState('geolocation', geoPermission.state);
      
      geoPermission.addEventListener('change', () => {
        this.updatePermissionState('geolocation', geoPermission.state);
      });

      // Check camera permission
      const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
      this.updatePermissionState('camera', cameraPermission.state);
      
      cameraPermission.addEventListener('change', () => {
        this.updatePermissionState('camera', cameraPermission.state);
      });

      // Check notifications permission
      const notificationPermission = await navigator.permissions.query({ name: 'notifications' as PermissionName });
      this.updatePermissionState('notifications', notificationPermission.state);
      
      notificationPermission.addEventListener('change', () => {
        this.updatePermissionState('notifications', notificationPermission.state);
      });

    } catch (error) {
      console.warn('Error al verificar permisos:', error);
    }
  }

  private updatePermissionState(permission: keyof DevicePermissions, state: PermissionState): void {
    this.permissionStates[permission] = {
      granted: state === 'granted',
      denied: state === 'denied',
      prompt: state === 'prompt'
    };
  }

  /**
   * Solicita permiso de geolocalización
   */
  async requestGeolocationPermission(): Promise<PermissionStatus> {
    try {
      if (!navigator.geolocation) {
        const error = 'Geolocalización no está disponible en este dispositivo';
        this.permissionStates.geolocation = { granted: false, denied: true, prompt: false, error };
        return this.permissionStates.geolocation;
      }

      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.permissionStates.geolocation = { granted: true, denied: false, prompt: false };
            resolve(this.permissionStates.geolocation);
          },
          (error) => {
            let errorMsg = 'Error al obtener ubicación';
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMsg = 'Permiso de ubicación denegado por el usuario';
                this.permissionStates.geolocation = { granted: false, denied: true, prompt: false, error: errorMsg };
                break;
              case error.POSITION_UNAVAILABLE:
                errorMsg = 'Información de ubicación no disponible';
                this.permissionStates.geolocation = { granted: false, denied: false, prompt: true, error: errorMsg };
                break;
              case error.TIMEOUT:
                errorMsg = 'Tiempo de espera agotado para obtener ubicación';
                this.permissionStates.geolocation = { granted: false, denied: false, prompt: true, error: errorMsg };
                break;
            }
            resolve(this.permissionStates.geolocation);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          }
        );
      });
    } catch (error) {
      const errorMsg = 'Error inesperado al solicitar ubicación';
      this.permissionStates.geolocation = { granted: false, denied: true, prompt: false, error: errorMsg };
      return this.permissionStates.geolocation;
    }
  }

  /**
   * Solicita permiso de cámara
   */
  async requestCameraPermission(): Promise<PermissionStatus> {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const error = 'Acceso a cámara no está disponible en este dispositivo';
        this.permissionStates.camera = { granted: false, denied: true, prompt: false, error };
        return this.permissionStates.camera;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      // Stop the stream immediately as we just needed permission
      stream.getTracks().forEach(track => track.stop());
      
      this.permissionStates.camera = { granted: true, denied: false, prompt: false };
      return this.permissionStates.camera;

    } catch (error: any) {
      let errorMsg = 'Error al acceder a la cámara';
      
      if (error.name === 'NotAllowedError') {
        errorMsg = 'Permiso de cámara denegado por el usuario';
        this.permissionStates.camera = { granted: false, denied: true, prompt: false, error: errorMsg };
      } else if (error.name === 'NotFoundError') {
        errorMsg = 'No se encontró una cámara en el dispositivo';
        this.permissionStates.camera = { granted: false, denied: true, prompt: false, error: errorMsg };
      } else if (error.name === 'NotReadableError') {
        errorMsg = 'La cámara está siendo usada por otra aplicación';
        this.permissionStates.camera = { granted: false, denied: false, prompt: true, error: errorMsg };
      } else {
        this.permissionStates.camera = { granted: false, denied: false, prompt: true, error: errorMsg };
      }
      
      return this.permissionStates.camera;
    }
  }

  /**
   * Solicita permiso de notificaciones
   */
  async requestNotificationPermission(): Promise<PermissionStatus> {
    try {
      if (!('Notification' in window)) {
        const error = 'Las notificaciones no están disponibles en este navegador';
        this.permissionStates.notifications = { granted: false, denied: true, prompt: false, error };
        return this.permissionStates.notifications;
      }

      if (Notification.permission === 'granted') {
        this.permissionStates.notifications = { granted: true, denied: false, prompt: false };
        return this.permissionStates.notifications;
      }

      if (Notification.permission === 'denied') {
        const error = 'Las notificaciones han sido bloqueadas previamente';
        this.permissionStates.notifications = { granted: false, denied: true, prompt: false, error };
        return this.permissionStates.notifications;
      }

      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        this.permissionStates.notifications = { granted: true, denied: false, prompt: false };
        
        // Mostrar notificación de bienvenida
        this.showWelcomeNotification();
      } else {
        const error = 'Permiso de notificaciones denegado';
        this.permissionStates.notifications = { granted: false, denied: true, prompt: false, error };
      }

      return this.permissionStates.notifications;

    } catch (error) {
      const errorMsg = 'Error al solicitar permiso de notificaciones';
      this.permissionStates.notifications = { granted: false, denied: true, prompt: false, error: errorMsg };
      return this.permissionStates.notifications;
    }
  }

  /**
   * Solicita permiso de almacenamiento persistente
   */
  async requestPersistentStoragePermission(): Promise<PermissionStatus> {
    try {
      if (!navigator.storage || !navigator.storage.persist) {
        const error = 'Almacenamiento persistente no está disponible';
        this.permissionStates.persistentStorage = { granted: false, denied: true, prompt: false, error };
        return this.permissionStates.persistentStorage;
      }

      const persistent = await navigator.storage.persist();
      
      if (persistent) {
        this.permissionStates.persistentStorage = { granted: true, denied: false, prompt: false };
      } else {
        const error = 'Almacenamiento persistente no fue otorgado';
        this.permissionStates.persistentStorage = { granted: false, denied: true, prompt: false, error };
      }

      return this.permissionStates.persistentStorage;

    } catch (error) {
      const errorMsg = 'Error al solicitar almacenamiento persistente';
      this.permissionStates.persistentStorage = { granted: false, denied: true, prompt: false, error: errorMsg };
      return this.permissionStates.persistentStorage;
    }
  }

  /**
   * Solicita todos los permisos de una vez
   */
  async requestAllPermissions(): Promise<DevicePermissions> {
    const results = await Promise.allSettled([
      this.requestGeolocationPermission(),
      this.requestCameraPermission(),
      this.requestNotificationPermission(),
      this.requestPersistentStoragePermission()
    ]);

    // Log results for debugging
    console.log('Resultados de permisos:', {
      geolocation: results[0],
      camera: results[1],
      notifications: results[2],
      persistentStorage: results[3]
    });

    return this.permissionStates;
  }

  /**
   * Obtiene el estado actual de todos los permisos
   */
  getPermissionStates(): DevicePermissions {
    return { ...this.permissionStates };
  }

  /**
   * Verifica si un permiso específico está otorgado
   */
  isPermissionGranted(permission: keyof DevicePermissions): boolean {
    return this.permissionStates[permission].granted;
  }

  /**
   * Verifica si todos los permisos esenciales están otorgados
   */
  areEssentialPermissionsGranted(): boolean {
    return this.permissionStates.geolocation.granted && 
           this.permissionStates.camera.granted;
  }

  /**
   * Muestra una notificación de bienvenida
   */
  private showWelcomeNotification(): void {
    if (this.permissionStates.notifications.granted) {
      this.showNotification('¡Bienvenido a BarClik!', {
        body: 'Ahora recibirás notificaciones sobre ofertas y actualizaciones.',
        tag: 'welcome',
        requireInteraction: false,
        silent: false
      });
    }
  }

  /**
   * Muestra una notificación usando Service Worker (funciona en iOS PWA + Android)
   * con fallback a new Notification() para navegadores de escritorio.
   */
  showNotification(title: string, options: NotificationOptions = {}): boolean {
    if (!this.permissionStates.notifications.granted) {
      console.warn('No se puede mostrar notificación: permiso no otorgado');
      return false;
    }

    const defaultOptions: NotificationOptions = {
      icon: '/assets/icons/icon-192x192.png',
      badge: '/assets/icons/icon-72x72.png',
      requireInteraction: false,
      silent: false,
      ...options
    };

    // Prefer ServiceWorker registration.showNotification() — required for iOS PWA
    // and also works on Android PWA. Falls back to new Notification() for desktop.
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification(title, defaultOptions);
      }).catch(() => {
        // Fallback if SW registration fails
        new Notification(title, defaultOptions);
      });
    } else {
      new Notification(title, defaultOptions);
    }
    return true;
  }

  /**
   * Obtiene información detallada del dispositivo
   */
  getDeviceInfo(): any {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      languages: navigator.languages,
      online: navigator.onLine,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      maxTouchPoints: navigator.maxTouchPoints,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: (navigator as any).deviceMemory,
      connection: (navigator as any).connection,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth,
        orientation: screen.orientation?.type
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio
      }
    };
  }
}